import React, { useEffect, useRef } from "react";
import { ArrowDownRight, ArrowUpRight, MapPin, Sparkles } from "lucide-react";
import { profile } from "../mock";
import { Button } from "./ui/button";
import useMagnetic from "../hooks/useMagnetic";
import { useRole } from "../context/RoleContext";

const CHIP_COLORS = ["#FF8A5B", "#FFD2A6", "#FFF3E9"];

const Hero = () => {
  const { hero } = useRole();
  const polaroidRef = useRef(null);
  const cta1 = useMagnetic({ strength: 0.28, radius: 160 });
  const cta2 = useMagnetic({ strength: 0.2, radius: 140 });

  // Mouse-follow spotlight + parallax tilt on polaroid
  useEffect(() => {
    const onMouseMove = (e) => {
      const root = document.documentElement;
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      root.style.setProperty("--mx", `${x}%`);
      root.style.setProperty("--my", `${y}%`);

      // parallax on polaroid
      if (polaroidRef.current) {
        const rx = (e.clientY / window.innerHeight - 0.5) * -8;
        const ry = (e.clientX / window.innerWidth - 0.5) * 8;
        polaroidRef.current.style.transform = `perspective(900px) rotate(3deg) rotateX(${rx}deg) rotateY(${ry}deg)`;
      }
    };
    window.addEventListener("mousemove", onMouseMove);
    return () => window.removeEventListener("mousemove", onMouseMove);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden spotlight"
    >
      {/* Animated morphing gradient blob */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -right-20 w-[520px] h-[520px] rounded-full blur-3xl opacity-40 blob-a" style={{ background: "radial-gradient(circle, #FF8A5B 0%, transparent 70%)" }} />
        <div className="absolute top-1/3 -left-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-35 blob-b" style={{ background: "radial-gradient(circle, #FFD2A6 0%, transparent 70%)" }} />
        <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full blur-3xl opacity-25 blob-c" style={{ background: "radial-gradient(circle, #FFF3E9 0%, transparent 70%)" }} />
      </div>
      <div className="grain absolute inset-0" />

      {/* Decorative monogram watermark */}
      <div
        aria-hidden
        className="monogram absolute -bottom-10 -right-6 md:-bottom-20 md:-right-10 text-[200px] md:text-[360px] leading-none"
      >
        TA.
      </div>

      <div className="relative container-x">
        {/* Status sticker row */}
        <div className="fade-up flex flex-wrap items-center gap-2 mb-8">
          <span
            data-testid="hero-status-chip"
            className="sticker tilt-l"
            style={{ background: "#FF8A5B", color: "#121826" }}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#121826] opacity-70 breathe" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#121826]" />
            </span>
            AVAILABLE · LONDON
          </span>
          <span
            className="sticker tilt-r-soft"
            style={{ background: "#FFD2A6", color: "#121826" }}
          >
            <Sparkles className="w-3 h-3" /> <span data-testid="hero-role-chip">{hero.chip}</span>
          </span>
          <span
            className="sticker tilt-l-soft"
            style={{ background: "#FFF3E9", color: "#121826" }}
          >
            AUG '27
          </span>
        </div>

        {/* Asymmetric hero grid */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-8">
            <h1
              data-testid="hero-headline"
              className="display fade-up text-[44px] leading-[0.96] sm:text-[60px] md:text-[78px] lg:text-[96px] text-[#FFF3E9]"
            >
              {hero.pre}{" "}
              <span className="serif-italic" style={{ color: "#FF8A5B" }}>
                {hero.accent}
              </span>
              <br />
              <span className="text-[#8B7B6E]">{hero.mid}</span>
              <span className="inline-block w-3 md:w-5" />
              <span className="scribble serif-italic" style={{ color: "#FFD2A6" }}>
                {hero.scribble}<span style={{ color: "#FF8A5B" }}>.</span>
              </span>
            </h1>

            <div className="fade-up delay-1 mt-10 max-w-xl">
              <p data-testid="hero-one-liner" className="text-[17px] md:text-[19px] leading-relaxed text-[#FFD2A6]">
                {hero.oneLiner}
              </p>
            </div>

            {/* CTAs */}
            <div className="fade-up delay-2 mt-8 flex flex-wrap gap-3">
              <span ref={cta1} className="inline-block transition-transform duration-200 ease-out">
                <Button
                  asChild
                  data-testid="hero-cta-contact"
                  className="h-12 px-6 rounded-full text-[14px] font-bold text-[#121826] hover:opacity-95"
                  style={{
                    background:
                      "linear-gradient(135deg, #FF8A5B 0%, #FFD2A6 100%)"
                  }}
                >
                  <a href="#contact" className="flex items-center gap-2">
                    {hero.cta}
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                </Button>
              </span>
              <span ref={cta2} className="inline-block transition-transform duration-200 ease-out">
                <Button
                  asChild
                  data-testid="hero-cta-cv"
                  variant="outline"
                  className="h-12 px-6 rounded-full bg-transparent text-[#EDEDED] text-[14px]"
                  style={{ borderColor: "rgba(255, 138, 91, 0.5)" }}
                >
                  <a
                    href={profile.cv}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 hover:text-[#FFD2A6]"
                  >
                    Grab the CV <ArrowDownRight className="w-4 h-4" />
                  </a>
                </Button>
              </span>
            </div>

            {/* Quick chip stats */}
            <div className="fade-up delay-3 mt-10 flex flex-wrap items-center gap-2">
              {hero.chips.map((label, i) => ({ label, color: CHIP_COLORS[i % CHIP_COLORS.length] })).map((c) => (
                <span
                  key={c.label}
                  className="chip"
                  style={{
                    borderColor: `${c.color}55`,
                    backgroundColor: `${c.color}10`,
                    color: "#E6E6EA"
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: c.color }}
                  />
                  {c.label}
                </span>
              ))}
            </div>
          </div>

          {/* Polaroid + sticker stack */}
          <div className="lg:col-span-4 fade-up delay-2 relative flex justify-center lg:justify-end">
            <div className="relative w-[260px] sm:w-[300px]">
              {/* Tape */}
              <div className="tape" style={{ top: -8, left: 28 }} aria-hidden />

              {/* Polaroid */}
              <div
                ref={polaroidRef}
                className="polaroid"
                style={{ transform: "rotate(3deg)", transition: "transform 200ms ease-out" }}
              >
                <div className="aspect-[4/5] overflow-hidden bg-[#1a1a1b]">
                  <img
                    src={profile.headshot}
                    alt="Tanmay Agarwal"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span
                    className="mono text-[10px] tracking-wider"
                    style={{ color: "#121826" }}
                  >
                    TANMAY · LDN
                  </span>
                  <span
                    className="serif-italic text-[14px]"
                    style={{ color: "#121826" }}
                  >
                    say hi.
                  </span>
                </div>
              </div>

              {/* Floating starburst */}
              <div
                className="absolute -top-6 -left-6 starburst spin-slow"
                aria-hidden
              >
                IMPERIAL<br />MBA '27
              </div>

              {/* Floating location chip */}
              <div
                className="absolute -bottom-4 -right-3 sticker tilt-r"
                style={{ background: "#121826", color: "#FFF3E9", border: "1px solid rgba(255,255,255,0.15)" }}
              >
                <MapPin className="w-3 h-3" style={{ color: "#FFD2A6" }} />
                LONDON, UK
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
